import React, { useEffect, useId, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ArrowRight } from 'lucide-react';
import { Button } from '../ds';
import { cx, focusRing } from '../ds/classes';
import { Brand } from './Logo';
import { AccountMenu } from './AccountMenu';
import { VerifyBanner } from './VerifyBanner';
import { useAuth } from '../../context/AuthContext';

const linkClass = (active) => cx(
  'inline-flex items-center min-h-target-min px-space-3 rounded-radius-md font-text text-small no-underline transition-colors duration-motion motion-reduce:transition-none',
  active ? 'bg-raised text-ink font-bold' : 'text-ink-soft hover:bg-raised hover:text-ink',
  focusRing
);

// Sonic Insight isn't here: Products and the home page's example both lead to it
const LINKS = [
  { to: '/products', label: 'Products' },
  { to: '/about', label: 'About' },
];

/** Into the studio: the one amber action for signed-in visitors */
const OpenStudio = () => (
  <Button as={Link} to="/projects" icon={ArrowRight} iconPosition="end">Open studio</Button>
);

/**
 * The site's top bar: the brand, the main pages, and signing up or going into the studio. On
 * narrow screens the links fold into a menu below the bar. The studio has its own bar instead.
 */
export const SiteNav = () => {
  const { user, isLoggedIn, logout } = useAuth();
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuId = useId();

  // A new page closes the menu
  useEffect(() => setMenuOpen(false), [pathname]);

  const links = LINKS.map((link) => (
    <Link key={link.to} to={link.to} aria-current={pathname === link.to ? 'page' : undefined} className={linkClass(pathname === link.to)}>
      {link.label}
    </Link>
  ));

  const signedOut = (
    <>
      <Link to="/login" className={linkClass(pathname === '/login')}>Log in</Link>
      <Button as={Link} to="/signup">Sign up free</Button>
    </>
  );

  return (
    <header className="sticky top-0 z-50 flex-none bg-chrome text-ink">
      <nav aria-label="Main" className="max-w-[1200px] mx-auto px-space-4 sm:px-space-6 py-space-3 flex items-center justify-between gap-space-4">
        <Brand />
        <div className="hidden md:flex items-center gap-space-1">{links}</div>
        <div className="hidden md:flex items-center gap-space-2">
          {isLoggedIn ? (
            <>
              <AccountMenu />
              <OpenStudio />
            </>
          ) : signedOut}
        </div>
        <Button
          variant="icon"
          icon={menuOpen ? X : Menu}
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={menuOpen}
          aria-controls={menuId}
          onClick={() => setMenuOpen(!menuOpen)}
          className="md:hidden"
        />
      </nav>
      <div
        id={menuId}
        hidden={!menuOpen}
        onKeyDown={(event) => event.key === 'Escape' && setMenuOpen(false)}
        className="md:hidden border-t border-raised"
      >
        <div className="max-w-[1200px] mx-auto px-space-4 sm:px-space-6 py-space-3 flex flex-col gap-space-1">
          {links}
          <div className="flex flex-wrap items-center gap-space-2 pt-space-3 mt-space-2 border-t border-raised">
            {isLoggedIn ? (
              <>
                {user?.email && <span className="flex-1 min-w-0 truncate text-caption text-muted">{user.email}</span>}
                <Button variant="secondary" onClick={logout}>Log out</Button>
                <OpenStudio />
              </>
            ) : signedOut}
          </div>
        </div>
      </div>
      <VerifyBanner centered />
    </header>
  );
};
